import { useCallback, useEffect, useState } from "react";
import { useBlocker } from "react-router-dom";

interface UseUnsavedChangesGuardResult {
  isPromptOpen: boolean;
  stayOnPage: () => void;
  leavePage: () => void;
}

export const useUnsavedChangesGuard = (
  isDirty: boolean,
): UseUnsavedChangesGuardResult => {
  const blocker = useBlocker(isDirty);
  const [isPromptOpen, setIsPromptOpen] = useState(false);

  useEffect(() => {
    if (blocker.state === "blocked") {
      setIsPromptOpen(true);
      return;
    }

    if (blocker.state === "unblocked") {
      setIsPromptOpen(false);
    }
  }, [blocker.state]);

  useEffect(() => {
    if (!isDirty) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isDirty]);

  const stayOnPage = useCallback(() => {
    if (blocker.state === "blocked") {
      blocker.reset();
    }
    setIsPromptOpen(false);
  }, [blocker]);

  const leavePage = useCallback(() => {
    if (blocker.state === "blocked") {
      blocker.proceed();
    }
    setIsPromptOpen(false);
  }, [blocker]);

  return {
    isPromptOpen,
    stayOnPage,
    leavePage,
  };
};
